import "@/App.css";
import { Suspense, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import { AppLayout } from "@/components/layout/AppLayout";
import { P, PageSkeleton } from "@/lib/lazyPages";
import LoginPage from "@/pages/LoginPage";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

// First page after login: last visited page (if still allowed) → role/user home → Dashboard
function Landing() {
  const { home, permsLoaded, pageAllowed, user } = useAuth();
  const navigate = useNavigate();
  const [decided, setDecided] = useState(() => !!sessionStorage.getItem("stancor_landed"));
  useEffect(() => {
    if (!permsLoaded || decided) return;
    sessionStorage.setItem("stancor_landed", "1");
    const last = localStorage.getItem(`stancor_last_route:${user?.username || ""}`);
    const ok = (p) => p && p !== "/" && (user?.is_admin || pageAllowed(p.split("?")[0]) || (home && p.split("?")[0] === home));
    const target = ok(last) ? last : ok(home) ? home : null;
    if (target) navigate(target, { replace: true });
    else setDecided(true);
  }, [permsLoaded, decided, home, user, pageAllowed, navigate]);
  if (!decided) return <div className="p-8 text-xs text-[#9CA3AF]" data-testid="landing-wait">Loading your workspace…</div>;
  return <P.Dashboard />;
}

function ProtectedRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#E32C17]" />
      </div>
    );
  }

  if (!isAuthenticated) return <LoginPage />;

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/workspace" element={<P.WorkspacePage />} />
        <Route path="/directors" element={<P.DirectorsPage />} />
        <Route path="/customers" element={<P.CustomersPage />} />
        <Route path="/customers/:name" element={<P.CustomerDetailPage />} />
        <Route path="/manufacturing" element={<P.ManufacturingPage />} />
        <Route path="/stock" element={<P.StockPage />} />
        <Route path="/stage-flow" element={<P.StageFlowPage />} />
        <Route path="/quality" element={<P.QualityPage />} />
        <Route path="/help" element={<P.HelpPage />} />
        <Route path="/purchase" element={<P.PurchasePage />} />
        <Route path="/jobwork" element={<P.JobWorkPage />} />
        <Route path="/size-analysis" element={<P.SizeAnalysisPage />} />
        <Route path="/fulfillment" element={<P.FulfillmentPage />} />
        <Route path="/chat" element={<P.ChatPage />} />
        <Route path="/sales-orders" element={<P.SalesOrderPage />} />
        <Route path="/order-book" element={<P.OrderBookPage />} />
        <Route path="/offers" element={<P.OffersPage />} />
        <Route path="/quotes" element={<P.QuotationsPage />} />
        <Route path="/dispatch" element={<P.DispatchPage />} />
        <Route path="/planning" element={<P.PlanningPage />} />
        <Route path="/planning-intake" element={<P.PlanningIntakePage />} />
        <Route path="/process-cards" element={<P.ProcessCardsPage />} />
        <Route path="/consumables" element={<P.ConsumablesPage />} />
        <Route path="/reconciliation" element={<P.ReconciliationPage />} />
        <Route path="/handover" element={<P.HandoverPage />} />
        <Route path="/jobs" element={<P.JobsPage />} />
        <Route path="/plugs" element={<P.PlugLogPage />} />
        <Route path="/go-live" element={<P.GoLivePage />} />
        <Route path="/broker-quotes" element={<P.QuotesPage />} />
        <Route path="/tasks" element={<P.TasksPage />} />
        <Route path="/data-fix" element={<P.DataFixPage />} />
        <Route path="/week-plan" element={<P.WeekPlanPage />} />
        <Route path="/mtc" element={<P.MtcPage />} />
        <Route path="/inspections" element={<P.InspectionsPage />} />
        <Route path="/incoming-rm" element={<P.IncomingRmPage />} />
        <Route path="/approvals" element={<P.ApprovalsPage />} />
        <Route path="/portal-requests" element={<P.PortalRequestsPage />} />
        <Route path="/prices-view" element={<P.PricesViewPage />} />
        <Route path="/standards" element={<Navigate to="/mtc?tab=standards" replace />} />
        <Route path="/daily-rates" element={<P.DailyRatesPage />} />
        <Route path="/dpr" element={<P.DprPage />} />
        <Route path="/production-entry" element={<P.ProdEntryPage />} />
        <Route path="/stock-register" element={<P.StockRegisterViewPage />} />
        <Route path="/settings" element={<P.SettingsPage />} />
        <Route path="/security-gate" element={<P.GateRegisterPage />} />
        <Route path="/hr" element={<P.HrPage />} /><Route path="/job-roles" element={<P.JobRolesPage />} /><Route path="/org-chart" element={<P.OrgChartPage />} /><Route path="/people-master" element={<P.PeopleMasterPage />} /><Route path="/reports-inbox" element={<P.ReportsPage />} /><Route path="/documents" element={<P.DocumentsPage />} />        <Route path="/manpower" element={<P.ManpowerPage />} />        <Route path="/maintenance" element={<P.MaintenancePage />} /><Route path="/complaints" element={<P.ComplaintsPage />} /><Route path="/projects" element={<P.ProjectsPage />} /><Route path="/amc" element={<P.AmcPage />} /><Route path="/settings/odoo-architecture" element={<P.OdooArchitecturePage />} />
      </Route>
        <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <Toaster position="top-right" richColors closeButton />
      <AuthProvider>
        <BrandingProvider>
        <BrowserRouter>
          <Suspense fallback={<PageSkeleton />}>
          {/* Public shared SO report — no auth required */}
          <Routes>
            <Route path="/so-share/:token" element={<P.SharedSOPage />} />
            <Route path="/quote-share/:token" element={<P.SharedQuotePage />} />
            <Route path="/verify/mtc/:token" element={<P.MtcVerifyPage />} />
            <Route path="/sign/:sid" element={<P.SignRemotePage />} />
            <Route path="/ack/:sid" element={<P.AckSignPage />} />
            <Route path="/jd/:sid" element={<P.JdIntakePage />} />
            <Route path="/org/:sid" element={<P.OrgReviewPage />} />
            <Route path="/portal" element={<P.PortalPage />} />
            <Route path="/prices" element={<P.BrokerPricesPage />} />
            <Route path="/work" element={<P.WorkEntryPage />} />
            <Route path="/stockreg" element={<P.StockRegisterPage />} />
            <Route path="/gate" element={<P.GatePinPage />} />
            <Route path="/visit/:token" element={<P.VisitorInvitePage />} />
            <Route path="/rates" element={<P.RatesPinPage />} />
            <Route path="/scan" element={<P.CameraScanPage />} />
            <Route path="/sop/bundles" element={<P.SopBundlesPage />} />
            <Route path="/wall" element={<P.BundleWallPage />} />
            <Route path="/scan/:bid" element={<P.ScanPage />} />
            <Route path="/floor/jobs" element={<P.JobsPage />} />
            <Route path="/floor/handover" element={<P.HandoverPage />} />
            <Route path="/floor/dpr" element={<P.FloorDprPage />} />
            <Route path="/floor/entry" element={<P.ProdEntryPage />} />
            <Route path="/tv/:slug" element={<P.TvScreenPage />} />
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
        </BrandingProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
